import { useState, useMemo } from 'react';
import { useDB } from '../../hooks/useDB';
import { formatDate, expiryStatus, daysUntil, initials, isSalarie } from '../../data/constants';

// ─── CATALOGUE COMPLET DES INDICATEURS DISPONIBLES ───────────────────────────
const PAGE_LABELS = {
  personnel:'Personnel & RH', benevoles:'Bénévoles', conges:'Congés & Soldes',
  absences:'Absences non planifiées', cartepro:'Cartes Pro & Diplômes',
  medecine:'Médecine du travail', entretiens:'Entretiens professionnels',
  formations:'Formations', onboarding:'Onboarding', contrats:'Contrats & Avenants',
  reunions:'Réunions',
};
const INDICATEURS_CATALOGUE = [
  // ── Personnel
  { id:'personnel_total',    label:'Personnel total',       icon:'👥', color:'',        categorie:'Personnel',    page:'personnel',
    compute:({staff})=>({ value: staff.filter(s=>s.statut==='Actif').length }) },
  { id:'salaries',           label:'Salariés (hors stag.)', icon:'💼', color:'cyan',     categorie:'Personnel',    page:'personnel',
    compute:({staff})=>({ value: staff.filter(s=>isSalarie(s)&&s.statut==='Actif'&&!['Stagiaire','Service civique','Bénévole'].includes(s.type)).length }) },
  { id:'stagiaires_civiques',  label:'Stagiaires & Civiques', icon:'🎓', color:'',         categorie:'Personnel',    page:'personnel',
    compute:({staff})=>({ value: staff.filter(s=>s.statut==='Actif'&&['Stagiaire','Service civique'].includes(s.type)).length }) },
  { id:'benevoles',          label:'Bénévoles actifs',      icon:'🤝', color:'purple',   categorie:'Personnel',    page:'benevoles',
    compute:({benevoles})=>({ value:(benevoles||[]).filter(b=>b.actif!==false).length }) },
  { id:'anciennete_moy',     label:'Ancienneté moy.',       icon:'📅', color:'',         categorie:'Personnel',    page:'personnel',
    compute:({staff})=>{
      const av=staff.filter(s=>s.dateDebut);
      if(!av.length) return {value:'—'};
      const moy=av.reduce((a,s)=>a+(Date.now()-new Date(s.dateDebut))/(1000*60*60*24*365.25),0)/av.length;
      return {value:`${moy.toFixed(1)} ans`};
    }},
  // ── Congés
  { id:'conges_attente',     label:'Congés en attente',     icon:'⏳', color:'warning',  categorie:'Congés',    page:'conges',
    compute:({conges})=>({ value:(conges||[]).filter(c=>['En attente M1','En attente M2','Valide M1','Valide M2'].includes(c.statut)).length }) },
  { id:'conges_mois',        label:'Congés ce mois',        icon:'🌴', color:'cyan',     categorie:'Congés',    page:'conges',
    compute:({conges})=>{ const n=new Date(); return {value:(conges||[]).filter(c=>{const d=new Date(c.dateDebut||c.debut);return d.getMonth()===n.getMonth()&&d.getFullYear()===n.getFullYear();}).length}; }},
  { id:'absences_np',        label:'Absences non prévues',  icon:'🚨', color:'danger',   categorie:'Congés',    page:'absences',
    compute:({absencesNP})=>({ value:(absencesNP||[]).length }) },
  // ── Documents & Médecine
  { id:'docs_alert',         label:'Docs à renouveler',     icon:'📄', color:'orange',   categorie:'Documents',    page:'cartepro',
    compute:({docs})=>({ value:(docs||[]).filter(d=>d.dateExpiration&&expiryStatus(d.dateExpiration)!=='ok').length }) },
  { id:'visites_dues',       label:'Visites médicales',     icon:'🏥', color:'danger',   categorie:'Médecine',    page:'medecine',
    compute:({medecine})=>({ value:(medecine||[]).filter(m=>m.prochaine&&daysUntil(m.prochaine)<=60).length, sub:'< 60 jours' }) },
  // ── Entretiens
  { id:'entretiens_planifies',label:'Entretiens planifiés', icon:'💬', color:'purple',   categorie:'Entretiens',    page:'entretiens',
    compute:({entretiens})=>({ value:(entretiens||[]).filter(e=>e.statut==='Planifié'||e.statut==='planifie').length }) },
  { id:'entretiens_retard',  label:'Entretiens en retard',  icon:'⚠️', color:'warning',  categorie:'Entretiens',    page:'entretiens',
    compute:({entretiens})=>({ value:(entretiens||[]).filter(e=>e.prochaine&&daysUntil(e.prochaine)<0).length }) },
  // ── Formations
  { id:'formations_planifiees',label:'Formations planifiées',icon:'🎓',color:'cyan',     categorie:'Formations',    page:'formations',
    compute:({formations})=>({ value:(formations||[]).filter(f=>f.statut==='Planifiée'||f.statut==='Planifié').length }) },
  { id:'formations_en_cours',label:'Formations en cours',   icon:'📚', color:'',         categorie:'Formations',    page:'formations',
    compute:({formations})=>({ value:(formations||[]).filter(f=>f.statut==='En cours').length }) },
  // ── Onboarding
  { id:'onboardings_en_cours',label:'Onboardings en cours', icon:'🚀', color:'cyan',     categorie:'Onboarding',    page:'onboarding',
    compute:({onboarding})=>({ value:(onboarding||[]).filter(o=>o.statut==='En cours').length }) },
  // ── Cartes professionnelles
  { id:'cartes_pro_ok',       label:'Cartes pro à jour',     icon:'🪪', color:'',         categorie:'Cartes pro',    page:'cartepro',
    compute:({docs})=>({ value:(docs||[]).filter(d=>d.type==='Carte Pro'&&d.dateExpiration&&expiryStatus(d.dateExpiration)==='ok').length }) },
  { id:'cartes_pro_echeance', label:'Cartes pro échéance',   icon:'🪪', color:'warning',  categorie:'Cartes pro',    page:'cartepro',
    compute:({docs})=>({ value:(docs||[]).filter(d=>d.type==='Carte Pro'&&d.dateExpiration&&expiryStatus(d.dateExpiration)==='expiring').length, sub:'< 60 jours' }) },
  { id:'cartes_pro_perimees', label:'Cartes pro périmées',   icon:'🪪', color:'danger',   categorie:'Cartes pro',    page:'cartepro',
    compute:({docs})=>({ value:(docs||[]).filter(d=>d.type==='Carte Pro'&&d.dateExpiration&&expiryStatus(d.dateExpiration)==='expired').length }) },
  // ── Contrats
  { id:'contrats_expirant',  label:'Contrats expirant',     icon:'📋', color:'warning',  categorie:'Contrats',    page:'contrats',
    compute:({contratsGen})=>({ value:(contratsGen||[]).filter(c=>c.dateFin&&daysUntil(c.dateFin)>=0&&daysUntil(c.dateFin)<=30).length, sub:'< 30 jours' }) },
  // ── Réunions
  { id:'reunions_mois',      label:'Réunions ce mois',      icon:'📆', color:'',         categorie:'Réunions',    page:'reunions',
    compute:({reunions})=>{ const n=new Date(); return {value:(reunions||[]).filter(r=>{const d=new Date(r.date);return d.getMonth()===n.getMonth()&&d.getFullYear()===n.getFullYear();}).length}; }},
];

const DEFAULT_ACTIFS = [
  'personnel_total','salaries','conges_attente','docs_alert',
  'visites_dues','entretiens_planifies','formations_planifiees','onboardings_en_cours',
];

// ─── MODAL CONFIGURATION INDICATEURS ─────────────────────────────────────────
function ModalConfigIndicateurs({ config, onSave, onClose }) {
  const [actifs, setActifs] = useState([...(config || DEFAULT_ACTIFS)]);
  const categories = [...new Set(INDICATEURS_CATALOGUE.map(i => i.categorie))];

  const toggle = id => setActifs(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const resetDefaut = () => setActifs([...DEFAULT_ACTIFS]);

  // Fermer sur clic fond
  const handleBackdrop = e => { if (e.target === e.currentTarget) onClose(); };

  return (
    <div
      onClick={handleBackdrop}
      style={{
        position:'fixed', inset:0, background:'rgba(0,20,60,0.6)',
        zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center',
        backdropFilter:'blur(3px)',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background:'#fff', borderRadius:18, width:'100%', maxWidth:600,
          maxHeight:'88vh', display:'flex', flexDirection:'column',
          boxShadow:'0 32px 96px rgba(0,48,135,0.3)',
          border:'1px solid rgba(0,48,135,0.1)',
          margin:'0 16px',
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding:'22px 28px 18px', borderBottom:'1px solid var(--border)',
          display:'flex', alignItems:'flex-start', justifyContent:'space-between', gap:12,
        }}>
          <div>
            <div style={{fontWeight:800, fontSize:'1.1rem', color:'var(--bleu)', marginBottom:4}}>
              ⚙️ Configurer les indicateurs du dashboard
            </div>
            <div style={{fontSize:'0.78rem', color:'var(--muted)'}}>
              Sélectionnez les indicateurs à afficher.{' '}
              <strong style={{color:actifs.length===0?'var(--danger)':'inherit'}}>
                {actifs.length} sélectionné{actifs.length > 1 ? 's' : ''}
              </strong>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background:'var(--bg2)', border:'1px solid var(--border)',
              width:34, height:34, borderRadius:'50%', cursor:'pointer',
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:'1rem', color:'var(--muted)', flexShrink:0,
            }}
          >✕</button>
        </div>

        {/* ── Liste scrollable ── */}
        <div style={{overflowY:'auto', flex:1, padding:'20px 28px'}}>
          {categories.map(cat => {
            const inds = INDICATEURS_CATALOGUE.filter(i => i.categorie === cat);
            return (
              <div key={cat} style={{marginBottom:24}}>
                <div style={{
                  fontSize:'0.68rem', fontWeight:800, textTransform:'uppercase',
                  letterSpacing:'0.1em', color:'var(--muted)', marginBottom:10,
                  display:'flex', alignItems:'center', gap:8,
                }}>
                  <span>{cat}</span>
                  <span style={{
                    background:'var(--bg2)', borderRadius:8, padding:'1px 7px',
                    fontSize:'0.65rem', fontWeight:700, color:'var(--muted)',
                  }}>
                    {inds.filter(i => actifs.includes(i.id)).length}/{inds.length}
                  </span>
                </div>
                <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))', gap:8}}>
                  {inds.map(ind => {
                    const on = actifs.includes(ind.id);
                    return (
                      <button
                        key={ind.id}
                        onClick={() => toggle(ind.id)}
                        style={{
                          display:'flex', alignItems:'center', gap:12,
                          padding:'12px 14px', borderRadius:12, cursor:'pointer',
                          border: on ? '2px solid var(--cyan)' : '2px solid var(--border)',
                          background: on ? 'rgba(0,174,239,0.06)' : '#fafbfc',
                          textAlign:'left', fontFamily:'inherit',
                          boxShadow: on ? '0 2px 8px rgba(0,174,239,0.12)' : 'none',
                        }}
                      >
                        <span style={{fontSize:'1.35rem', flexShrink:0}}>{ind.icon}</span>
                        <div style={{flex:1, minWidth:0}}>
                          <div style={{
                            fontSize:'0.8rem', fontWeight:700, lineHeight:1.3,
                            color: on ? 'var(--bleu)' : 'var(--text)',
                          }}>{ind.label}</div>
                        </div>
                        <div style={{
                          width:20, height:20, borderRadius:'50%', flexShrink:0,
                          background: on ? 'var(--cyan)' : 'var(--border)',
                          display:'flex', alignItems:'center', justifyContent:'center',
                          color:'#fff', fontSize:'0.7rem', fontWeight:800,
                        }}>{on ? '✓' : ''}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Footer ── */}
        <div style={{
          padding:'16px 28px', borderTop:'1px solid var(--border)',
          display:'flex', alignItems:'center', justifyContent:'space-between', gap:10,
        }}>
          <button
            onClick={resetDefaut}
            style={{
              background:'none', border:'1px solid var(--border)', borderRadius:8,
              padding:'7px 14px', cursor:'pointer', fontSize:'0.78rem',
              color:'var(--muted)', fontFamily:'inherit',
            }}
          >↩ Rétablir les défauts</button>
          <div style={{display:'flex', gap:10}}>
            <button onClick={onClose} className="btn btn-secondary">Annuler</button>
            <button
              onClick={() => { onSave(actifs); onClose(); }}
              className="btn btn-primary"
              disabled={actifs.length === 0}
            >✓ Enregistrer</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── BARRE D'INDICATEURS CONFIGURABLES ───────────────────────────────────────
function StatRow({ data, isRH, showConfig, onToggleConfig, config, setConfig, onNavigate }) {
  const indicateursActifs = useMemo(() =>
    INDICATEURS_CATALOGUE.filter(i => (config || DEFAULT_ACTIFS).includes(i.id)),
    [config]
  );
  const computed = useMemo(() =>
    indicateursActifs.map(ind => ({ ...ind, ...ind.compute(data) })),
    [indicateursActifs, data]
  );

  return (
    <div style={{marginBottom:20}}>
      {/* Titre ligne + bouton config */}
      <div style={{
        display:'flex', alignItems:'center', justifyContent:'space-between',
        marginBottom:10,
      }}>
        <div style={{fontSize:'0.72rem', fontWeight:700, textTransform:'uppercase',
          letterSpacing:'0.08em', color:'var(--muted)'}}>
          Indicateurs RH
        </div>
        {isRH && (
          <button
            onClick={onToggleConfig}
            style={{
              display:'flex', alignItems:'center', gap:6,
              background:'var(--bg2)', border:'1px solid var(--border)',
              borderRadius:20, padding:'5px 12px', cursor:'pointer',
              fontSize:'0.75rem', fontWeight:700, color:'var(--bleu)',
              fontFamily:'inherit',
            }}
          >
            ⚙️ Personnaliser
          </button>
        )}
      </div>
      <div className="stats-row">
        {computed.map(ind => (
          <div
            key={ind.id}
            className={`stat-card ${ind.color || ''}`}
            onClick={() => ind.page && onNavigate && onNavigate(ind.page)}
            style={ind.page && onNavigate ? { cursor: 'pointer', transition: 'transform .15s, box-shadow .15s' } : {}}
            onMouseEnter={e => { if (ind.page && onNavigate) { e.currentTarget.style.transform='translateY(-2px)'; e.currentTarget.style.boxShadow='0 6px 18px rgba(0,48,135,0.15)'; }}}
            onMouseLeave={e => { if (ind.page && onNavigate) { e.currentTarget.style.transform=''; e.currentTarget.style.boxShadow=''; }}}
            title={ind.page && onNavigate ? `→ ${PAGE_LABELS[ind.page] || ind.page}` : undefined}
          >
            <div style={{fontSize:'1.2rem', marginBottom:2}}>{ind.icon}</div>
            <div className="stat-value">{ind.value}</div>
            <div className="stat-label">{ind.label}</div>
            {ind.sub && <div style={{fontSize:'0.65rem', opacity:0.7, marginTop:2}}>{ind.sub}</div>}
          </div>
        ))}
        {computed.length === 0 && (
          <div style={{
            padding:'24px', color:'var(--muted)', fontSize:'0.82rem',
            fontStyle:'italic', width:'100%', textAlign:'center',
          }}>
            Aucun indicateur sélectionné — cliquez sur ⚙️ Personnaliser pour en ajouter.
          </div>
        )}
      </div>
    </div>
  );
}

// ─── ALERTES ─────────────────────────────────────────────────────────────────
function AlertBanners({ docs, medecine, entretiens, sessionWarn, prolongerSession, handleLogout }) {
  const expired  = docs.filter(d=>d.dateExpiration&&expiryStatus(d.dateExpiration)==='expired');
  const expiring = docs.filter(d=>d.dateExpiration&&expiryStatus(d.dateExpiration)==='expiring');
  const overdueV = medecine.filter(m=>m.prochaine&&daysUntil(m.prochaine)<0);
  const soonV    = medecine.filter(m=>m.prochaine&&daysUntil(m.prochaine)>=0&&daysUntil(m.prochaine)<=30);
  const dueE     = entretiens.filter(e=>e.prochaine&&daysUntil(e.prochaine)>=0&&daysUntil(e.prochaine)<=30);
  return (
    <div style={{marginBottom:16}}>
      {expired.length>0   && <div className="alert-banner alert-danger">🚨 {expired.length} document(s) expiré(s) : {expired.map(d=>d.nom+' ('+d.type+')').join(', ')}</div>}
      {expiring.length>0  && <div className="alert-banner alert-warning">⚠️ {expiring.length} document(s) expirant bientôt : {expiring.map(d=>d.nom+' ('+d.type+')').join(', ')}</div>}
      {overdueV.length>0  && <div className="alert-banner alert-danger">🏥 {overdueV.length} visite(s) médicale(s) en retard : {overdueV.map(v=>v.nom).join(', ')}</div>}
      {soonV.length>0     && <div className="alert-banner alert-warning">📅 {soonV.length} visite(s) dans les 30 jours : {soonV.map(v=>v.nom).join(', ')}</div>}
      {dueE.length>0      && <div className="alert-banner alert-warning">💬 {dueE.length} entretien(s) à planifier : {dueE.map(e=>e.nom).join(', ')}</div>}
    </div>
  );
}

// ─── DASHBOARD RH ────────────────────────────────────────────────────────────
function DashboardRH({ staff, events, conges, docs, medecine, entretiens,
  sessionWarn, prolongerSession, handleLogout,
  remunerations, formations, benevoles, absencesNP, reunions, onboarding, contratsGen,
  myStaffId, myUserId, isRH, accesCoord, onNavigate,
  configIndicateurs: configIndicateursProp, setConfigIndicateurs: setConfigIndicateursProp }) {

  // Utilise les props si fournies (état géré dans App.js), sinon useDB local (fallback)
  const [configLocal, setConfigLocal] = useDB('cmob_dashboard_indicateurs', DEFAULT_ACTIFS);
  const configIndicateurs    = configIndicateursProp    !== undefined ? configIndicateursProp    : configLocal;
  const setConfigIndicateurs = setConfigIndicateursProp !== undefined ? setConfigIndicateursProp : setConfigLocal;
  const [showConfig, setShowConfig] = useState(false);

  // ── Filtrage manager : rattachés nativement OU exception manuelle ──────────
  const isManager = !isRH && !!myUserId;
  const now_mgr = new Date();
  const estVisible = (s) => {
    if (!isManager) return true;
    const mgrs = s.managers || s.managerIds || [];
    if (mgrs.some(m => String(m === Object(m) ? m.staffId : m) === String(myStaffId))) return true;
    return (accesCoord || []).some(a =>
      String(a.managerId) === String(myUserId) &&
      String(a.staffId)   === String(s.id) &&
      (a.permanent || !a.dateFin || new Date(a.dateFin) >= now_mgr)
    );
  };
  const staffV     = isManager ? staff.filter(estVisible) : staff;
  const congesV    = isManager ? conges.filter(c => staffV.some(s => String(s.id) === String(c.staffId))) : conges;
  const docsV      = isManager ? docs.filter(d => staffV.some(s => String(s.id) === String(d.staffId)))   : docs;
  const medecineV  = isManager ? medecine.filter(m => staffV.some(s => String(s.id) === String(m.staffId))): medecine;
  const entretiensV= isManager ? entretiens.filter(e => staffV.some(s => String(s.id) === String(e.staffId))): entretiens;

  const prochains = [...events].filter(e=>new Date(e.date)>=new Date())
    .sort((a,b)=>new Date(a.date)-new Date(b.date)).slice(0,4);
  const TCOLOR = {Entraînement:'cyan',Match:'red',Compétition:'bleu',Tournoi:'bleu',Réunion:'cyan'};
  const data = {staff:staffV,conges:congesV,docs:docsV,medecine:medecineV,entretiens:entretiensV,formations,benevoles,absencesNP,reunions,onboarding,contratsGen,remunerations};

  return (
    <div>
      {sessionWarn && (
        <div style={{position:'fixed',top:0,left:0,right:0,zIndex:9999,
          background:'linear-gradient(135deg,#b45309,#d97706)',color:'#fff',
          padding:'12px 20px',display:'flex',alignItems:'center',justifyContent:'space-between',
          gap:12,boxShadow:'0 2px 12px rgba(0,0,0,0.2)'}}>
          <span style={{fontWeight:700,fontSize:'0.88rem'}}>
            ⏱️ Votre session expire dans moins de 15 minutes — pensez à sauvegarder votre travail.
          </span>
          <div style={{display:'flex',gap:8}}>
            <button onClick={prolongerSession} style={{background:'rgba(255,255,255,0.2)',
              border:'1.5px solid rgba(255,255,255,0.5)',color:'#fff',borderRadius:7,
              padding:'5px 14px',cursor:'pointer',fontWeight:700,fontSize:'0.82rem'}}>🔄 Prolonger (8h)</button>
            <button onClick={handleLogout} style={{background:'rgba(255,255,255,0.1)',
              border:'1.5px solid rgba(255,255,255,0.3)',color:'#fff',borderRadius:7,
              padding:'5px 14px',cursor:'pointer',fontSize:'0.82rem'}}>Se déconnecter</button>
          </div>
        </div>
      )}
      <div className="divider-cyan"/>
      <AlertBanners docs={docsV} medecine={medecineV} entretiens={entretiensV}
        sessionWarn={sessionWarn} prolongerSession={prolongerSession} handleLogout={handleLogout}/>

      {/* ── INDICATEURS CONFIGURABLES ── */}
      <StatRow
        data={data} isRH={true}
        config={configIndicateurs}
        setConfig={setConfigIndicateurs}
        showConfig={showConfig}
        onToggleConfig={() => setShowConfig(true)}
        onNavigate={onNavigate}
      />
      {showConfig && (
        <ModalConfigIndicateurs
          config={configIndicateurs || DEFAULT_ACTIFS}
          onSave={setConfigIndicateurs}
          onClose={() => setShowConfig(false)}
        />
      )}

      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:18}}>
        <div className="card">
          <div className="card-header"><span className="card-title">📅 Prochains événements</span></div>
          {prochains.length===0?<div className="empty"><div className="empty-icon">📭</div>Aucun</div>:
          <table><tbody>{prochains.map(e=>(
            <tr key={e.id}>
              <td><div style={{fontWeight:700,color:'var(--bleu)'}}>{e.titre}</div><div style={{fontSize:'0.75rem',color:'var(--muted)'}}>{e.section}</div></td>
              <td><span className={`badge badge-${TCOLOR[e.type]||'gray'}`}>{e.type}</span></td>
              <td style={{fontSize:'0.78rem',color:'var(--muted)',whiteSpace:'nowrap'}}>{formatDate(e.date)}</td>
            </tr>
          ))}</tbody></table>}
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">🌴 Dernières demandes de congés</span></div>
          {congesV.length===0?<div className="empty"><div className="empty-icon">🌴</div>Aucune</div>:
          <table><tbody>{[...congesV].slice(-4).reverse().map(c=>(
            <tr key={c.id}>
              <td style={{fontWeight:700}}>{c.nom}</td>
              <td><span className={`badge ${c.type==='Arrêt maladie'?'badge-red':c.type==='RTT'?'badge-cyan':'badge-bleu'}`}>{c.type}</span></td>
              <td><StatutBadge statut={c.statut}/></td>
              <td style={{fontSize:'0.78rem',color:'var(--muted)'}}>{c.jours}j</td>
            </tr>
          ))}</tbody></table>}
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD SALARIÉ ───────────────────────────────────────────────────────
function DashboardSalarie({ user, conges, soldes, docs, medecine, entretiens, formations, staff, onNavigate }) {
  const sid = String(user?.staffId||'');
  const monProfil     = (staff||[]).find(s=>String(s.id)===sid);
  const mesConges     = conges.filter(c=>String(c.staffId)===sid);
  const monSolde      = soldes.find(s=>String(s.staffId)===sid);
  const mesDocs       = docs.filter(d=>String(d.staffId)===sid);
  const mesVisites    = medecine.filter(m=>String(m.staffId)===sid).sort((a,b)=>new Date(b.dateVisite||b.date)-new Date(a.dateVisite||a.date));
  const maVisite      = mesVisites[0];
  const mesEntr       = entretiens.filter(e=>String(e.staffId)===sid).sort((a,b)=>new Date(b.dateEntretien||b.date)-new Date(a.dateEntretien||a.date));
  const mesFormations = (formations||[]).filter(f=>String(f.staffId)===sid);
  const docsAlert     = mesDocs.filter(d=>d.dateExpiration&&expiryStatus(d.dateExpiration)!=='ok');
  const STATUTS_APPROUVES = ['Approuvé RH','Approuve RH','APPROUVÉ'];
  const cpPrisReel  = mesConges.filter(c => STATUTS_APPROUVES.includes(c.statut) && ['CP','Congés payés','Congé payé'].includes(c.type))
    .reduce((s,c) => s + (Number(c.jours)||0), 0);
  const rttPrisReel = mesConges.filter(c => STATUTS_APPROUVES.includes(c.statut) && c.type === 'RTT')
    .reduce((s,c) => s + (Number(c.jours)||0), 0);
  const cpRestants    = monSolde ? (Number(monSolde.cpAcquis)||0) + (Number(monSolde.cpReportes)||0) - cpPrisReel : null;
  const rttRestants   = monSolde ? (Number(monSolde.rttAcquis)||0) - rttPrisReel : null;
  const congesAttente = mesConges.filter(c=>['En attente M1','En attente M2','Validé manager','Valide M1','Valide M2'].includes(c.statut)).length;
  const congesOk      = mesConges.filter(c=>c.statut==='Approuvé RH'||c.statut==='Approuve RH').length;
  const prochainEntr  = mesEntr.find(e=>['planifie','amont_collab','amont_manager','jour_j'].includes(e.statut));
  const goConges      = ()=>onNavigate&&onNavigate('mes-conges');
  const TYPE_LABELS   = {EAE:'Entretien annuel',EP:'Entretien professionnel',MI:'Mi-année',PE:"Fin d'essai",REP:'Reprise'};
  const anciennete    = monProfil?.dateDebut?(()=>{
    const ms=Date.now()-new Date(monProfil.dateDebut);
    const ans=Math.floor(ms/(1000*60*60*24*365.25));
    const mois=Math.floor((ms%(1000*60*60*24*365.25))/(1000*60*60*24*30.44));
    return ans>0?`${ans} an${ans>1?'s':''} ${mois>0?mois+'mois':''}`.trim():`${mois} mois`;
  })():null;
  const prochaineVisite = maVisite?.prochaine;
  const joursVisite     = prochaineVisite?daysUntil(prochaineVisite):null;

  return (
    <div>
      <div style={{background:'linear-gradient(135deg,#003087 0%,#0055cc 60%,#0077bb 100%)',
        borderRadius:16,padding:'24px 28px',marginBottom:20,color:'#fff',
        display:'flex',alignItems:'center',gap:20,flexWrap:'wrap',
        boxShadow:'0 4px 24px rgba(0,48,135,0.25)'}}>
        <div style={{width:64,height:64,borderRadius:'50%',flexShrink:0,
          background:'rgba(255,255,255,0.2)',display:'flex',alignItems:'center',
          justifyContent:'center',fontSize:'1.6rem',fontWeight:800,
          border:'3px solid rgba(255,255,255,0.4)',letterSpacing:1}}>
          {initials(user.prenom,(user.nom||'').split(' ')[0]||'')}
        </div>
        <div style={{flex:1}}>
          <div style={{fontSize:'1.4rem',fontWeight:800,marginBottom:4}}>Bonjour {user.prenom} 👋</div>
          <div style={{opacity:0.85,fontSize:'0.85rem',lineHeight:1.6}}>
            {monProfil?.poste&&<span>{monProfil.poste} · </span>}
            {monProfil?.section&&<span>{monProfil.section} · </span>}
            {monProfil?.type&&<span style={{background:'rgba(255,255,255,0.2)',padding:'1px 8px',borderRadius:8,fontSize:'0.75rem'}}>{monProfil.type}</span>}
          </div>
          {anciennete&&(<div style={{marginTop:8,fontSize:'0.78rem',opacity:0.75}}>🗓 Ancienneté : <strong>{anciennete}</strong>{monProfil?.dateDebut&&` (depuis le ${formatDate(monProfil.dateDebut)})`}</div>)}
        </div>
        <div style={{textAlign:'right',opacity:0.8,fontSize:'0.78rem'}}>
          <div>{new Date().toLocaleDateString('fr-FR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</div>
          <div style={{marginTop:4,fontWeight:700,fontSize:'0.85rem'}}>CMOB</div>
        </div>
      </div>
      {docsAlert.map(d=>(<div key={d.id} className={`alert-banner ${expiryStatus(d.dateExpiration)==='expired'?'alert-danger':'alert-warning'}`}>{expiryStatus(d.dateExpiration)==='expired'?'⛔':'⚠️'} Document <strong>{d.type}</strong> {expiryStatus(d.dateExpiration)==='expired'?'expiré':'expire dans '+daysUntil(d.dateExpiration)+' jour(s)'}</div>))}
      {joursVisite!==null&&joursVisite<=30&&joursVisite>=0&&(<div className="alert-banner alert-warning">🏥 Visite médicale prévue le <strong>{formatDate(prochaineVisite)}</strong> — dans {joursVisite} jour(s)</div>)}
      {prochainEntr&&(<div className="alert-banner" style={{background:'rgba(0,48,135,0.07)',borderColor:'rgba(0,48,135,0.2)',color:'var(--bleu)'}}>💬 Entretien <strong>{TYPE_LABELS[prochainEntr.type]||prochainEntr.type}</strong> en cours{prochainEntr.dateEntretien&&` — prévu le ${formatDate(prochainEntr.dateEntretien)}`}</div>)}
      <div className="stats-row" style={{marginBottom:20}}>
        {[
          {val:cpRestants!==null?cpRestants:'—',label:'CP restants',icon:'🌴',cls:cpRestants!==null&&cpRestants<3?'warning':'cyan'},
          {val:rttRestants!==null?rttRestants:'—',label:'RTT restants',icon:'☀️',cls:''},
          {val:congesOk,label:'Congés approuvés',icon:'✅',cls:''},
          {val:congesAttente,label:'En attente',icon:'⏳',cls:congesAttente>0?'warning':''},
          {val:mesDocs.length,label:'Mes documents',icon:'📄',cls:''},
          {val:mesFormations.length,label:'Formations',icon:'🏆',cls:''},
        ].map(k=>(<div key={k.label} className={`stat-card ${k.cls}`}><div style={{fontSize:'1.3rem',marginBottom:4}}>{k.icon}</div><div className="stat-value">{k.val}</div><div className="stat-label">{k.label}</div></div>))}
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:18,marginBottom:18}}>
        <div className="card">
          <div className="card-header">
            <span className="card-title">🌴 Mes congés récents</span>
            {monSolde&&(<div style={{display:'flex',gap:8,fontSize:'0.72rem'}}>
              <span style={{background:'rgba(26,127,55,0.1)',color:'#1a7f37',padding:'2px 8px',borderRadius:8,fontWeight:700}}>CP : {cpRestants}j restants</span>
              {rttRestants>0&&(<span style={{background:'rgba(0,119,187,0.1)',color:'#0077bb',padding:'2px 8px',borderRadius:8,fontWeight:700}}>RTT : {rttRestants}j</span>)}
            </div>)}
          </div>
          {mesConges.length===0?(<><div style={{padding:'10px 16px',textAlign:'center'}}><button onClick={goConges} style={{padding:'8px 20px',borderRadius:8,cursor:'pointer',fontFamily:'inherit',fontWeight:700,fontSize:'0.82rem',border:'none',background:'var(--bleu)',color:'#fff'}}>+ Nouvelle demande de congé</button></div><div className="empty" style={{paddingTop:4}}><div className="empty-icon">🌴</div>Aucune demande en cours</div></>):
          (<table><tbody>{[...mesConges].sort((a,b)=>new Date(b.dateDebut)-new Date(a.dateDebut)).slice(0,5).map(c=>(<tr key={c.id}><td><span className={`badge ${c.type==='Arrêt maladie'?'badge-red':c.typeConge==='RTT'||c.type==='RTT'?'badge-cyan':'badge-bleu'}`}>{c.typeConge||c.type}</span></td><td style={{fontSize:'0.78rem',color:'var(--muted)'}}>{formatDate(c.dateDebut||c.debut)} → {formatDate(c.dateFin||c.fin)}</td><td style={{fontWeight:700,whiteSpace:'nowrap'}}>{c.jours||c.nbJours||'?'}j</td><td><StatutBadge statut={c.statut}/></td></tr>))}</tbody></table>)}
        </div>
        <div className="card">
          <div className="card-header"><span className="card-title">📄 Mes documents</span></div>
          {mesDocs.length===0?(<div className="empty"><div className="empty-icon">📄</div>Aucun document</div>):
          (<table><tbody>{mesDocs.map(d=>{const s=d.dateExpiration?expiryStatus(d.dateExpiration):null;const j=d.dateExpiration?daysUntil(d.dateExpiration):null;return(<tr key={d.id}><td style={{fontWeight:700,fontSize:'0.82rem'}}>{d.type}</td><td style={{fontSize:'0.75rem',color:'var(--muted)'}}>{d.organisme||'—'}</td><td>{!d.dateExpiration?<span className="doc-chip">♾️ Permanent</span>:s==='expired'?<span className="doc-chip expired">⛔ Expiré</span>:s==='expiring'?<span className="doc-chip expiring">⚠️ {j}j</span>:<span className="doc-chip">✅ {j}j</span>}</td></tr>);})}</tbody></table>)}
        </div>
      </div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(260px,1fr))',gap:18,marginBottom:18}}>
        <div className="card">
          <div className="card-header"><span className="card-title">💬 Mes entretiens</span></div>
          {mesEntr.length===0?(<div className="empty"><div className="empty-icon">💬</div>Aucun entretien</div>):
          (<div style={{padding:'8px 0'}}>{mesEntr.slice(0,4).map(e=>{const stColors={finalise:['#dcfce7','#166534'],planifie:['#dbeafe','#1e40af'],amont_collab:['#fef9c3','#854d0e'],amont_manager:['#fef9c3','#854d0e'],jour_j:['#e0f2fe','#0369a1'],annule:['#f3f4f6','#6b7280']};const [bg,col]=stColors[e.statut]||['#f3f4f6','#374151'];return(<div key={e.id} style={{padding:'8px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:10}}><div style={{flex:1}}><div style={{fontWeight:600,fontSize:'0.83rem'}}>{TYPE_LABELS[e.type]||e.type}</div><div style={{fontSize:'0.72rem',color:'var(--muted)'}}>{e.managerNom&&`Avec ${e.managerNom}`}{e.dateEntretien&&` · ${formatDate(e.dateEntretien)}`}</div></div><span style={{fontSize:'0.7rem',fontWeight:700,padding:'2px 8px',borderRadius:10,background:bg,color:col,whiteSpace:'nowrap'}}>{e.statut==='finalise'?'✅ Finalisé':e.statut==='planifie'?'📅 Planifié':e.statut==='amont_collab'||e.statut==='amont_manager'?'✏️ En cours':e.statut==='jour_j'?'🎯 Jour J':'—'}</span></div>);})}</div>)}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:18}}>
          <div className="card" style={{flex:1}}>
            <div className="card-header"><span className="card-title">🏥 Suivi médical</span></div>
            {!maVisite?(<div className="empty"><div className="empty-icon">🏥</div>Aucune visite enregistrée</div>):
            (<div style={{padding:'14px 18px'}}><div style={{fontWeight:700,fontSize:'0.85rem',marginBottom:6}}>{maVisite.type} — {formatDate(maVisite.dateVisite||maVisite.date)}</div>{maVisite.medecin&&<div style={{fontSize:'0.75rem',color:'var(--muted)',marginBottom:8}}>Dr {maVisite.medecin}</div>}<span className={`badge ${(maVisite.resultat||maVisite.aptitude||'').includes('Inapte')?'badge-red':(maVisite.resultat||maVisite.aptitude||'').includes('réserve')?'badge-yellow':'badge-green'}`}>{maVisite.resultat||maVisite.aptitude||'—'}</span>{prochaineVisite&&(<div style={{marginTop:10,padding:'8px 10px',borderRadius:8,background:joursVisite<0?'rgba(220,38,38,0.08)':joursVisite<=30?'rgba(180,83,9,0.08)':'rgba(26,127,55,0.08)',fontSize:'0.78rem',color:joursVisite<0?'var(--danger)':joursVisite<=30?'var(--warning)':'var(--success)'}}>{joursVisite<0?`⛔ Visite en retard de ${Math.abs(joursVisite)}j`:joursVisite<=30?`⚠️ Prochaine dans ${joursVisite}j — ${formatDate(prochaineVisite)}`:`✅ Prochaine le ${formatDate(prochaineVisite)}`}</div>)}</div>)}
          </div>
          <div className="card" style={{flex:1}}>
            <div className="card-header"><span className="card-title">🏆 Formations</span></div>
            {mesFormations.length===0?(<div className="empty" style={{padding:'12px'}}><div className="empty-icon">🏆</div>Aucune formation</div>):
            (<div style={{padding:'4px 0'}}>{mesFormations.slice(0,3).map(f=>(<div key={f.id} style={{padding:'7px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',gap:8}}><div style={{flex:1}}><div style={{fontWeight:600,fontSize:'0.82rem'}}>{f.titre||f.formation}</div><div style={{fontSize:'0.7rem',color:'var(--muted)'}}>{f.organisme}{f.dateDebut?` · ${formatDate(f.dateDebut)}`:''}</div></div><span className={`badge ${f.statut==='Terminée'?'badge-green':f.statut==='En cours'?'badge-cyan':'badge-gray'}`} style={{fontSize:'0.65rem'}}>{f.statut||'—'}</span></div>))}</div>)}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────
// eslint-disable-next-line no-unused-vars
function isDoubleManager(conge) { return (conge.managers||[]).length>=2; }

// eslint-disable-next-line no-unused-vars
function WorkflowSteps({ conge }) {
  const s=conge.statut||'';const managers=conge.managers||[];const validations=conge.validationsManager||[];const double=managers.length>=2;
  const steps=[{label:'Demande\nsalarié',key:'demande',icon:'👤'}];
  if(managers.length===0){steps.push({label:'Validation\nRH',key:'rh',icon:'🔐'});}
  else{managers.forEach((m,i)=>{steps.push({label:`Validation\n${m.nom.split(' ')[0]}${double?' (M'+(i+1)+')':''}`,key:`m${i+1}`,icon:'👔',managerIdx:i});});steps.push({label:'Validation\nRH',key:'rh',icon:'🔐'});}
  const getState=key=>{if(key==='demande')return'done';if(key==='rh'){if(s==='Approuve RH')return'done';if(s==='Refuse RH')return'rejected';if(s==='Valide M1'&&!double)return'active';if(s==='Valide M2')return'active';if(managers.length===0)return'active';return'pending';}const idx=parseInt(key.replace('m',''))-1;const rank=idx+1;const val=validations[idx];if(val?.statut==='valide')return'done';if(val?.statut==='refuse')return'rejected';if(s===`En attente M${rank}`)return'active';if(rank===1&&s==='En attente M1')return'active';return'pending';};
  return(<div className="workflow-steps" style={{overflowX:'auto',paddingBottom:4}}>{steps.map(step=>{const state=getState(step.key);const icons={done:'✓',active:'●',rejected:'✗',pending:'○'};const val=step.managerIdx!==undefined?validations[step.managerIdx]:null;return(<div key={step.key} className={`wf-step ${state}`}><div className="wf-dot">{icons[state]||'○'}</div><div className="wf-label" style={{whiteSpace:'pre-line',textAlign:'center'}}>{step.label}</div>{val?.commentaire&&<div className="wf-comment">"{val.commentaire}"</div>}{step.key==='rh'&&conge.commentaireRH&&<div className="wf-comment">"{conge.commentaireRH}"</div>}</div>);})}</div>);
}

function StatutBadge({ statut }) {
  const map={'En attente M1':['badge-yellow','⏳ En attente manager 1'],'Valide M1':['badge-cyan','👔 Validé M1'],'Refuse M1':['badge-red','✗ Refusé M1'],'En attente M2':['badge-orange','⏳ En attente manager 2'],'Valide M2':['badge-purple','👔 Validé M1+M2'],'Refuse M2':['badge-red','✗ Refusé M2'],'Approuve RH':['badge-green','✓ Approuvé'],'Refuse RH':['badge-red','✗ Refusé RH'],'En attente manager':['badge-yellow','⏳ En attente manager'],'Valide manager':['badge-cyan','👔 Validé manager'],'Refuse manager':['badge-red','✗ Refusé manager'],'Approuvé RH':['badge-green','✓ Approuvé'],'Refusé RH':['badge-red','✗ Refusé']};
  const [cls,label]=map[statut]||['badge-gray',statut];
  return <span className={`badge ${cls}`}>{label}</span>;
}

// eslint-disable-next-line no-unused-vars
function MesConges({ user, conges, setConges, soldes, users }) {}

export { AlertBanners, DashboardRH, DashboardSalarie };
